import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { logisticApi } from 'redux/services/logistic'

import { AppLoader } from 'components/AppLoader'

import { ReactComponent as CloseIcon } from 'assets/images/svg/close_circle.svg'
import { ReactComponent as LinkIcon } from 'assets/images/svg/link.svg'
import { ReactComponent as LockIcon } from 'assets/images/svg/lock.svg'
import { ReactComponent as RotateIcon } from 'assets/images/svg/rotate.svg'

import styles from './styles.module.scss'

type Props = {
  image: {
    id: number
    file: string
  }
  onClose: () => void
  editable?: boolean
}

export const CanvasModal = ({ image, onClose, editable }: Props) => {
  const [updateFile, { isLoading }] = logisticApi.useUpdateFileMutation()

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [img, setImg] = useState<HTMLImageElement | null>(null)

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [])

  useEffect(() => {
    const img = new Image()
    img.src = image.file
    img.onload = handleOnLoad
    setImg(img)
  }, [image.file])

  const handleOnLoad = (e: Event) => {
    const target = e.target as HTMLImageElement
    if (!target) return
    setImg(target)

    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')

    if (context && canvas) {
      canvas.width = target.width
      canvas.height = target.height
      context.drawImage(target, 0, 0, target.width, target.height)
    }
  }

  const reset = () => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!context || !img || !canvas) return

    canvas.width = img.width
    canvas.height = img.height
    context.save()
    context.translate(canvas.width / 2, canvas.height / 2)
    context.rotate((0 * Math.PI) / 180)
    context.drawImage(
      img,
      -img.width / 2,
      -img.height / 2,
      img.width,
      img.height,
    )
    context.restore()
  }

  const rotateRightTo90 = () => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!context || !img || !canvas) return

    canvas.width = img.height
    canvas.height = img.width
    context.save()
    context.translate(canvas.width / 2, canvas.height / 2)
    context.rotate((90 * Math.PI) / 180)
    context.drawImage(
      img,
      -img.width / 2,
      -img.height / 2,
      img.width,
      img.height,
    )
    context.restore()
  }

  const rotateLeftTo90 = () => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!context || !img || !canvas) return

    canvas.width = img.height
    canvas.height = img.width
    context.save()
    context.translate(canvas.width / 2, canvas.height / 2)
    context.rotate((-90 * Math.PI) / 180)
    context.drawImage(
      img,
      -img.width / 2,
      -img.height / 2,
      img.width,
      img.height,
    )
    context.restore()
  }

  const save = () => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!context || !img || !canvas) return

    canvas.toBlob(blob => {
      if (!blob) return

      const fileName = image.file.substr(image.file.lastIndexOf('/') + 1)
      const file = new File([blob], fileName)

      const formData = new FormData()
      formData.append('file_id', image.id.toString())
      formData.append('file', file)
      updateFile(formData)
        .unwrap()
        .then(() => {
          onClose()
        })
    })
  }

  return createPortal(
    <div className={styles.container}>
      <div className={styles.close} onClick={onClose}>
        <CloseIcon />
      </div>
      <canvas width={0} height={0} ref={canvasRef} className={styles.canvas} />

      {/* <img src={image.file} alt="" /> */}

      <div className={styles.controls}>
        <a href={image.file} target="_blank" rel="noopener noreferrer">
          <div className={styles.control}>
            <LinkIcon />
          </div>
        </a>
        {editable && (
          <>
            <div
              className={styles.control}
              style={{ transform: 'rotatey(180deg)' }}
              onClick={rotateLeftTo90}
            >
              <RotateIcon />
            </div>
            <div className={styles.control} onClick={reset}>
              <CloseIcon />
            </div>
            <div className={styles.control} onClick={rotateRightTo90}>
              <RotateIcon />
            </div>
            <div className={styles.control} onClick={save}>
              {isLoading ? <AppLoader /> : <LockIcon />}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
